/**
 * Auto-Confirm Booking Types
 * Type definitions for barber booking auto-confirmation settings
 * and per-customer trust levels
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * How the barber handles incoming booking confirmations
 */
export enum BookingAutoConfirmMode {
  AlwaysAutoConfirm = 0,
  AlwaysRequireReview = 1,
  AutoConfirmReturning = 2,
}

/**
 * Per-customer trust level override for booking confirmation
 */
export enum CustomerTrustLevel {
  UseDefault = 0,
  Trusted = 1,
  RequiresReview = 2,
  Blocked = 3,
}

// ============================================================================
// Customer Settings Types
// ============================================================================

/**
 * Barber's settings for a specific customer
 */
export interface BarberCustomerSettings {
  id?: number;
  customerId: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  trustLevel: CustomerTrustLevel;
  privateNote?: string;
  blockReason?: string;
  createdAt: string;
  updatedAt: string;
  // Booking stats
  totalBookings: number;
  completedBookings: number;
  noShows: number;
  lastVisit?: string;
}

/**
 * Request to update customer settings
 */
export interface UpdateCustomerSettingsRequest {
  trustLevel: CustomerTrustLevel;
  privateNote?: string;
  blockReason?: string;
}

/**
 * Query parameters for customer settings list
 */
export interface CustomerSettingsQueryParams {
  trustLevel?: CustomerTrustLevel;
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Paginated list of customer settings
 */
export interface CustomerSettingsList {
  items: BarberCustomerSettings[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/**
 * Summary counts of customers by trust level
 */
export interface CustomerSettingsSummary {
  totalCustomers: number;
  trustedCount: number;
  requiresReviewCount: number;
  blockedCount: number;
  defaultCount: number;
}

// ============================================================================
// Pending Booking Types
// ============================================================================

/**
 * A booking awaiting barber confirmation
 */
export interface PendingBooking {
  id: number;
  bookingDateTime: string;
  durationMinutes: number;
  status: string;
  notes?: string;
  totalPrice: number;
  barbershopId: number;
  barbershopName: string;
  barberId: number;
  barberName: string;
  userId: string;
  userName: string;
  userEmail: string;
  services: PendingBookingService[];
  pendingExpiresAt?: string;
  createdAt: string;
}

/**
 * Service included in a pending booking
 */
export interface PendingBookingService {
  serviceId: number;
  serviceName: string;
  priceCharged: number;
  durationMinutes: number;
}

/**
 * Request to decline a booking
 */
export interface DeclineBookingRequest {
  reason?: string;
}

// ============================================================================
// Auto-Confirm Settings Types
// ============================================================================

/**
 * Barber's auto-confirm settings (part of barber profile)
 */
export interface AutoConfirmSettings {
  bookingAutoConfirmMode: BookingAutoConfirmMode;
  pendingBookingExpirationHours: number;
}

/**
 * Request to update auto-confirm settings
 */
export interface UpdateAutoConfirmSettingsRequest {
  bookingAutoConfirmMode?: BookingAutoConfirmMode;
  pendingBookingExpirationHours?: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display label for BookingAutoConfirmMode
 */
export const getAutoConfirmModeLabel = (mode: BookingAutoConfirmMode): string => {
  switch (mode) {
    case BookingAutoConfirmMode.AlwaysAutoConfirm:
      return 'Auto-confirm all bookings';
    case BookingAutoConfirmMode.AlwaysRequireReview:
      return 'Require manual review for all bookings';
    case BookingAutoConfirmMode.AutoConfirmReturning:
      return 'Auto-confirm returning customers only';
    default:
      return 'Unknown';
  }
};

/**
 * Get description for BookingAutoConfirmMode
 */
export const getAutoConfirmModeDescription = (mode: BookingAutoConfirmMode): string => {
  switch (mode) {
    case BookingAutoConfirmMode.AlwaysAutoConfirm:
      return 'All booking requests are automatically confirmed without your review';
    case BookingAutoConfirmMode.AlwaysRequireReview:
      return 'All booking requests require your manual confirmation before they are finalized';
    case BookingAutoConfirmMode.AutoConfirmReturning:
      return 'First-time customers require review, returning customers are auto-confirmed';
    default:
      return '';
  }
};

/**
 * Get display label for CustomerTrustLevel
 */
export const getTrustLevelLabel = (level: CustomerTrustLevel): string => {
  switch (level) {
    case CustomerTrustLevel.UseDefault:
      return 'Use Default';
    case CustomerTrustLevel.Trusted:
      return 'Trusted';
    case CustomerTrustLevel.RequiresReview:
      return 'Requires Review';
    case CustomerTrustLevel.Blocked:
      return 'Blocked';
    default:
      return 'Unknown';
  }
};

/**
 * Get color class for CustomerTrustLevel
 */
export const getTrustLevelColor = (level: CustomerTrustLevel): string => {
  switch (level) {
    case CustomerTrustLevel.UseDefault:
      return 'text-gray-600 bg-gray-100';
    case CustomerTrustLevel.Trusted:
      return 'text-green-700 bg-green-100';
    case CustomerTrustLevel.RequiresReview:
      return 'text-yellow-700 bg-yellow-100';
    case CustomerTrustLevel.Blocked:
      return 'text-red-700 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

/**
 * Calculate time remaining until expiration
 */
export const getTimeUntilExpiration = (expiresAt?: string): string | null => {
  if (!expiresAt) return null;

  const now = new Date();
  const expiration = new Date(expiresAt);
  const diffMs = expiration.getTime() - now.getTime();

  if (diffMs <= 0) return 'Expired';

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  }
  return `${minutes}m remaining`;
};
